import {
  v2RechargeRefundRefundOrder,
  v2RechargeRefundRefundBatch,
  v2RechargeRefundRefundConfirm,
  v2RechargeRefundRefundAbandon,
  v2RechargeRefundRefundDetailByOrderId,
  v2RechargeRefundRefundCalcPreview,
  v2RechargeRefundRefundDraft,
  v2RechargeRefundRefundBatchDraft,
  v2RechargeRefundRefundCheckRefundable,
  v2RechargeRefundRefundBatchDetail,
  outsourceV2RechargeRefundRefundOrder,
  outsourceV2RechargeRefundRefundBatch,
  outsourceV2RechargeRefundRefundConfirm,
  outsourceV2RechargeRefundRefundAbandon,
  outsourceV2RechargeRefundRefundDetailByOrderId,
  outsourceV2RechargeRefundRefundCalcPreview,
  outsourceV2RechargeRefundRefundDraft,
  outsourceV2RechargeRefundRefundBatchDraft,
  outsourceV2RechargeRefundRefundBatchDetail,
  outsourceV2RechargeRefundRefundCheckRefundable,
} from 'services/api';
import { $sType } from '@mpc/common/src/utils/sType';
import type {
  RefundSubmitCmd,
  RefundBatchSubmitCmd,
  RefundConfirmCmd,
  RefundDraftQuery,
  RefundBatchDraftCmd,
  RefundCheckCmd,
  CurrencyEnum,
} from 'services/api';

/**
 * 退款单 API 封装（内包/外包复用，$sType 区分接口）
 *
 * 设计依据：frontend-design.md §7.1 API 命名 + 用户指示「接口要区分内外包的接口，使用 $sType 进行区分」。
 *
 * 接口路径（genapi 产物，swagger 生成）：
 * - 内包：/api/v2/recharge-refund/refund/*（v2RechargeRefundRefund*）
 * - 外包：/api/outsource/v2/recharge-refund/refund/*（outsourceV2RechargeRefundRefund*，
 *   后端 OutsourceRefundController，pnpm genapi local 从本地后端 swagger 生成）
 *
 * 外包控制器 6 接口：order/batch/confirm/abandon/detail/{orderId}/calc-preview，
 * 全部 isExternal=true（外包字段过滤在 VO 层 applyExternalFilter）。
 */
// ⚠️ $sType 由 app 入口 setSType('outer'/'inner') 在顶层调用设置；此处若模块顶层缓存 isOuter，
// 静态 import 链（RouteRegister→OutWorkbenchPortal→FormDrawer→本文件）会早于 setSType 求值，
// 外包端拿到 inner → 走内包接口（实证：外包 refundDetail 请求了 /api/v2/recharge-refund/refund/detail）。
// 必须每次调用动态判断。
const isOuter = () => $sType === 'outer';

export const refundSubmit = (
  data: RefundSubmitCmd,
  opts?: Parameters<typeof v2RechargeRefundRefundOrder>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundOrder(data, opts)
    : v2RechargeRefundRefundOrder(data, opts);

export const refundBatchSubmit = (
  data: RefundBatchSubmitCmd,
  opts?: Parameters<typeof v2RechargeRefundRefundBatch>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundBatch(data, opts)
    : v2RechargeRefundRefundBatch(data, opts);

export const refundConfirm = (
  data: RefundConfirmCmd,
  opts?: Parameters<typeof v2RechargeRefundRefundConfirm>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundConfirm(data, opts)
    : v2RechargeRefundRefundConfirm(data, opts);

export const refundAbandon = (
  orderId: number,
  opts?: Parameters<typeof v2RechargeRefundRefundAbandon>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundAbandon({ order_id: orderId }, opts)
    : v2RechargeRefundRefundAbandon({ order_id: orderId }, opts);

export const refundDetail = (orderId: number) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundDetailByOrderId({ order_id: orderId })
    : v2RechargeRefundRefundDetailByOrderId({ order_id: orderId });

/** 批量详情（批量确认弹框打开时调，替代前端逐单拉 refundDetail，参考充值 fetchRechargeBatchDetail） */
export const refundBatchDetail = (orderIds: number[]) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundBatchDetail({ order_ids: orderIds })
    : v2RechargeRefundRefundBatchDetail({ order_ids: orderIds });

export const refundCalcPreview = (
  l2DemandId: number,
  amount: number,
  currency: CurrencyEnum
) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundCalcPreview({
        l2_demand_id: l2DemandId,
        amount,
        currency,
      })
    : v2RechargeRefundRefundCalcPreview({
        l2_demand_id: l2DemandId,
        amount,
        currency,
      });

/**
 * 发起退款草稿（GET /draft，不落库）：以二级单为入参返回草稿预填数据 + 前置校验。
 * 基于二级单发起（l2_demand_id 必填）；三级单已生成时传 l3_demand_id（无三级单不传）；
 * 退回后重新发起传 refund_order_id 带入上次内容。
 */
export const refundDraft = (params: RefundDraftQuery) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundDraft(params)
    : v2RechargeRefundRefundDraft(params);

/**
 * 批量退款草稿（POST /batch-draft，不落库）：按二级/三级单批量生成草稿预填数据。
 * 任一单据不满足退款准入则整体失败（对齐批量发起语义）。
 */
export const refundBatchDraft = (data: RefundBatchDraftCmd) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundBatchDraft(data)
    : v2RechargeRefundRefundBatchDraft(data);

/**
 * 批量判断能否退款（POST /check-refundable）：发起退款前逐单校验准入维度（需求存在/适用范围/节点/预算调整/在途/余额），
 * 任一不满足返回 refundable=false + reason，前端据此报错且不打开弹框。
 * 内包走 /api/v2/.../check-refundable，外包走 /api/outsource/v2/.../check-refundable（$sType 区分，对齐其它退款接口）。
 * opts 透传接口配置（如 { alertError: false }，对齐充值批量 draft 的手动报错通道）。
 */
export const refundCheckRefundable = (
  data: RefundCheckCmd,
  opts?: Parameters<typeof v2RechargeRefundRefundCheckRefundable>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRefundCheckRefundable(data, opts)
    : v2RechargeRefundRefundCheckRefundable(data, opts);

/**
 * 单个单据折算 check-refundable 入参（发起退款前校验）。
 * <p>
 * 后端 check-refundable 支持二级/三级单混合传入：l3DemandIds 走 checkRefundableByL3（用三级单反查父二级，
 * 携带 l2+l3 维度校验，含三级单剩余可退余额 L3_WITH_L2 merge）；l2DemandIds 走 checkRefundableByL2（仅三级单无关系）。
 * 因此三级单场景必须传三级单号，否则校验维度错误（bug：校验用二级单、草稿却用二级+三级，三级单余额查不到）。
 * 规则（用户明确）：判断是否有三级单，优先传三级单号；若无三级单号，则传二级单号。
 */
export function toRefundCheckParam(
  l2DemandId: number,
  l3DemandId?: number | null
): RefundCheckCmd {
  return l3DemandId != null
    ? { l3_demand_ids: [l3DemandId] }
    : { l2_demand_ids: [l2DemandId] };
}

/**
 * 批量单据折算 check-refundable 入参：逐行判断，有三级单放 l3_demand_ids，无三级单放 l2_demand_ids。
 * 后端支持 l2/l3 混合批量（至少传一个）；空数组字段不传，避免"至少传一个"语义被空数组破坏。
 */
export function toRefundCheckBatchParam(
  rows: Array<{ l2: number; l3?: number | null }>
): RefundCheckCmd {
  const l2: number[] = [];
  const l3: number[] = [];
  rows.forEach((r) => {
    if (r.l3 != null) l3.push(r.l3);
    else if (r.l2 != null) l2.push(r.l2);
  });
  return {
    ...(l2.length ? { l2_demand_ids: l2 } : {}),
    ...(l3.length ? { l3_demand_ids: l3 } : {}),
  };
}
